import type { MenuItemData } from '../../data/domains/foodMenu';
import { triggerMenuItemHaptic, type MenuItemHealthHaptic } from './tactile';

/** Circular M/S badge (collapsed). */
export const MEAL_SNACK_TAG_SHORT_SIZE = 20;
export const MEAL_SNACK_TAG_BORDER_RADIUS = 10;
export const MEAL_SNACK_TAG_OVERLAY_BG = 'rgba(0,0,0,0.6)';
/** Expanded meal/snack + selection (#n) tag height when selected. */
export const MEAL_SNACK_TAG_LONG_HEIGHT = 18;
export const MEAL_SNACK_TAG_LONG_PADDING_H = 7;
export const MEAL_SNACK_TAG_LONG_PADDING_V = 3.5;
/** Detail expand control (bottom-trailing image corner). */
export const MEAL_SNACK_TAG_EXPAND_BTN_SIZE = 24;
export const MEAL_SNACK_TAG_EXPAND_BTN_RADIUS = 6;
export const MEAL_SNACK_TAG_EXPAND_ICON_SIZE = 12;

/** Stable long-tag width without onLayout (avoids 1-frame flicker). */
export function estimateMealSnackTagLongWidth(label: string): number {
  return Math.min(120, Math.max(36, label.length * 7 + 14));
}

export const MENU_FLOW_ITEM_CARD_HEIGHT = 126;
export const MENU_FLOW_ITEM_CARD_MARGIN_VERTICAL = 8;
export const MENU_FLOW_ITEM_ROW_HEIGHT = MENU_FLOW_ITEM_CARD_HEIGHT + 2 * MENU_FLOW_ITEM_CARD_MARGIN_VERTICAL;
export const MENU_FLOW_ITEM_CARD_BORDER_WIDTH = 1;
export const MENU_FLOW_ITEM_CARD_INNER_HEIGHT = MENU_FLOW_ITEM_CARD_HEIGHT - 2 * MENU_FLOW_ITEM_CARD_BORDER_WIDTH;
export const MENU_FLOW_ITEM_CARD_INSET = 4;
export const MENU_FLOW_ITEM_IMAGE_GAP = MENU_FLOW_ITEM_CARD_INSET;
export const MENU_FLOW_ITEM_IMAGE_PADDING = MENU_FLOW_ITEM_CARD_INSET;
export const MENU_FLOW_ITEM_IMAGE_WIDTH = MENU_FLOW_ITEM_CARD_INNER_HEIGHT - 2 * MENU_FLOW_ITEM_CARD_INSET;
export const MENU_FLOW_ITEM_IMAGE_SECTION_WIDTH = MENU_FLOW_ITEM_IMAGE_WIDTH + 2 * MENU_FLOW_ITEM_CARD_INSET;
/**
 * Extra padding for title + description only.
 * Does not inset scaled-carbs (top corner) or qty (bottom corner).
 */
export const MENU_FLOW_ITEM_CONTENT_PADDING_H = 0;
export const MENU_FLOW_ITEM_CONTENT_PADDING_V = 10;
export const MENU_FLOW_ITEM_DESCRIPTION_MARGIN_TOP = 4;
export const MENU_FLOW_ITEM_BOTTOM_ROW_PADDING_TOP = 4;
export const MENU_FLOW_ITEM_BOTTOM_ROW_MIN_HEIGHT = 32;
export const MENU_FLOW_ITEM_MACRO_GAP = 6;
/** Shared footprint for scaled-carbs + qty steppers (same total width). */
export const MENU_FLOW_ITEM_STEPPER_BTN_SIZE = 22;
export const MENU_FLOW_ITEM_STEPPER_HEIGHT = 24;
export const MENU_FLOW_ITEM_STEPPER_VALUE_WIDTH = 28;
export const MENU_FLOW_ITEM_STEPPER_WIDTH =
  MENU_FLOW_ITEM_STEPPER_BTN_SIZE * 2 + MENU_FLOW_ITEM_STEPPER_VALUE_WIDTH;
export const MENU_FLOW_ITEM_TITLE_MIN_HEIGHT = 24;
export const MENU_FLOW_ITEM_BADGE_INSET = 6;
export const MENU_FLOW_ITEM_TITLE_FONT_SIZE = 16;
export const MENU_FLOW_ITEM_DESC_FONT_SIZE = 12;
export const MENU_FLOW_ITEM_MACRO_VALUE_FONT_SIZE = 12;
export const MENU_FLOW_ITEM_MACRO_LABEL_FONT_SIZE = 9;

export const MENU_FLOW_ITEM_IMAGE_BORDER_RADIUS = 20;
export const MENU_ITEM_CARD_BORDER_RADIUS = 24;
export const MENU_ITEM_TAG_BORDER_RADIUS = 20;

export const MENU_FLOW_ITEM_IMAGE_BORDER_RADIUS_SELECTED = 14;
export const MENU_ITEM_CARD_BORDER_RADIUS_SELECTED = 18;
export const MENU_ITEM_TAG_BORDER_RADIUS_SELECTED = 20;
// export const MENU_FLOW_ITEM_IMAGE_BORDER_RADIUS = 20;
// export const MENU_FLOW_ITEM_IMAGE_BORDER_RADIUS = 20;

export function parseMacro(val: unknown): number {
  const num = typeof val === 'string' ? parseFloat(val) : Number(val);
  return Number.isNaN(num) ? 0 : Math.round(num);
}

export function getHealthCodeFlags(item: MenuItemData): {
  isAllergy: boolean;
  isDislike: boolean;
  showBadge: boolean;
} {
  const code = typeof (item as { health_code?: number }).health_code === 'number' ? (item as { health_code: number }).health_code : 0;
  return {
    isAllergy: code === 1 || code === 10,
    isDislike: code === 2 || code === 20,
    showBadge: code !== 0,
  };
}

type QuantitySource = 'card' | 'stepper';

export type QuantityIntentContext = {
  quantity: number;
  isForceFixedMenu: boolean;
  selectionDisabled: boolean;
  wouldExceedLimit: boolean;
  menuBuilderId: number;
  itemId: number;
  limitMessage: string;
  isAllergy: boolean;
  isDislike: boolean;
  onQuantityChange: (menu_builder_id: number, itemId: number, isPlusClick: boolean) => void;
  onSelectionDisabledInteraction?: () => void;
  onLimitExceeded?: (message: string) => void;
  triggerShake: (direction: 'left' | 'right') => void;
  onCardQuantityZeroLimit?: () => void;
};

function healthFromCtx(ctx: QuantityIntentContext): MenuItemHealthHaptic {
  if (ctx.isAllergy) {
    return 'allergy';
  }
  if (ctx.isDislike) {
    return 'dislike';
  }
  return 'none';
}

/** Card tap: 0→1 / 1→0 / shake at limit. Stepper: standard +/-. */
export function applyQuantityIntent(
  intent: 'increment' | 'decrement',
  source: QuantitySource,
  touchDirection: 'left' | 'right',
  ctx: QuantityIntentContext,
): void {
  if (ctx.isForceFixedMenu) {
    return;
  }
  if (ctx.selectionDisabled) {
    ctx.onSelectionDisabledInteraction?.();
    return;
  }

  const health = healthFromCtx(ctx);

  if (intent === 'decrement') {
    if (ctx.quantity < 1) {
      return;
    }
    triggerMenuItemHaptic('quantity', health);
    ctx.onQuantityChange(ctx.menuBuilderId, ctx.itemId, false);
    return;
  }

  if (source === 'stepper') {
    if (ctx.wouldExceedLimit) {
      ctx.triggerShake('right');
      ctx.onLimitExceeded?.(ctx.limitMessage);
      return;
    }
    triggerMenuItemHaptic('quantity', health);
    ctx.onQuantityChange(ctx.menuBuilderId, ctx.itemId, true);
    return;
  }

  if (ctx.quantity === 0) {
    if (ctx.wouldExceedLimit) {
      ctx.onCardQuantityZeroLimit?.();
      ctx.triggerShake(touchDirection);
      ctx.onLimitExceeded?.(ctx.limitMessage);
      return;
    }
    triggerMenuItemHaptic('quantity', health);
    ctx.onQuantityChange(ctx.menuBuilderId, ctx.itemId, true);
    return;
  }
  if (ctx.quantity === 1) {
    triggerMenuItemHaptic('quantity', health);
    ctx.onQuantityChange(ctx.menuBuilderId, ctx.itemId, false);
    return;
  }
  if (ctx.wouldExceedLimit) {
    ctx.triggerShake(touchDirection);
  }
}
